import api, { LIST, STOREFRONT_SETTINGS, STOREFRONT_STYLES } from '../Api'
import { ICollectionResponse } from "../../models/ICollectionResponse";
import { IStorefrontStyle, IStorefrontStyleSaveRequest } from "../../models/Storefront";
import { IActionResponse } from "../../dal/BaseDAL";

// Storefront Styles — named, reusable element style presets (see
// components/Storefront/Editor/stylesConfig.ts for what a style is and how refs resolve).
//
// Scope travels on the doc's `storefront` field (the sections precedent): a storefront id, or
// `global` for org-wide styles. The list endpoint returns the storefront's OWN styles plus the
// org's globals, so every mutation invalidates the same storefront-keyed list tag.
//
// The composed, read-only `stylesConfig` blob the shopper renderer reads is part of the
// calculated storefront settings, so mutations also invalidate STOREFRONT_SETTINGS — otherwise a
// cached settings response would keep serving a stale styles blob to the preview/publish flow.
const storefrontStyleApi = api.injectEndpoints({
    endpoints: (build) => ({
        // Own storefront styles ∪ org global styles. Global docs come back with storefront === 'global'.
        getStorefrontStyles: build.query<ICollectionResponse<IStorefrontStyle>, string>({
            query: (storefrontId) => `storefront/storefronts/${storefrontId}/styles`,
            providesTags: (_result, _error, storefrontId) => [
                { type: STOREFRONT_STYLES, id: storefrontId },
                { type: STOREFRONT_STYLES, id: LIST },
            ]
        }),
        // Create (no id) or update (id) — one hook so the panel's save path doesn't branch.
        saveStorefrontStyle: build.mutation<IActionResponse, { style: IStorefrontStyleSaveRequest, storefront: string }>({
            query: (arg) => arg.style.id
                ? {
                    url: `storefront/storefronts/styles/${arg.style.id}`,
                    method: 'PUT',
                    body: arg.style
                }
                : {
                    url: 'storefront/storefronts/styles',
                    method: 'POST',
                    body: arg.style
                },
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_STYLES, id: arg.storefront },
                { type: STOREFRONT_STYLES, id: LIST },
                { type: STOREFRONT_SETTINGS, id: arg.storefront },
            ]
        }),
        deleteStorefrontStyle: build.mutation<IActionResponse, { id: string, storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/styles/${arg.id}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_STYLES, id: arg.storefront },
                { type: STOREFRONT_STYLES, id: LIST },
                { type: STOREFRONT_SETTINGS, id: arg.storefront },
            ]
        }),
        // Re-scope a storefront style to `global`. The id is PRESERVED, so every existing
        // `$styleRef` keeps resolving with zero rewrites. Invalidate the whole list tag: the
        // promoted style is now visible to every storefront of the org.
        promoteStorefrontStyle: build.mutation<IActionResponse, { id: string, storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/styles/${arg.id}/promote`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_STYLES, id: arg.storefront },
                { type: STOREFRONT_STYLES, id: LIST },
                { type: STOREFRONT_SETTINGS, id: arg.storefront },
            ]
        }),
        // Make a `palette` / `fontPack` doc THE active one for its storefront: the API sets its
        // isActive flag and clears every same-type sibling in the same scope, so activation is a
        // single call rather than a read-modify-write of the whole list. Only storefront/preview
        // scoped docs may be activated (a global or template-bundled doc is rejected with
        // success:false + a message the panel surfaces).
        activateStorefrontStyle: build.mutation<IActionResponse, { id: string, storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/styles/${arg.id}/activate`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_STYLES, id: arg.storefront },
                { type: STOREFRONT_STYLES, id: LIST },
                { type: STOREFRONT_SETTINGS, id: arg.storefront },
            ]
        }),
        // Theme-derived starter styles ("Add starter styles" on an empty category).
        // Returns the CREATED styles as a collection (not an ActionResponse) — only element types
        // that had no style yet are seeded, so the list may be empty on a re-run.
        seedStorefrontStyles: build.mutation<ICollectionResponse<IStorefrontStyle>, { storefront: string }>({
            query: (arg) => ({
                url: `storefront/storefronts/${arg.storefront}/styles/seed`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_STYLES, id: arg.storefront },
                { type: STOREFRONT_STYLES, id: LIST },
                { type: STOREFRONT_SETTINGS, id: arg.storefront },
            ]
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontStylesQuery,
    useSaveStorefrontStyleMutation,
    useDeleteStorefrontStyleMutation,
    usePromoteStorefrontStyleMutation,
    useActivateStorefrontStyleMutation,
    useSeedStorefrontStylesMutation,
} = storefrontStyleApi
